/* eslint-disable multiline-ternary */
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment/moment'
import { Fragment, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Col,
  Form,
  FormFeedback,
  Label,
  Row,
} from 'reactstrap'
import { InputCurrency, InputNumber, InputText } from '../../../components/inputs'
import toast from 'react-hot-toast'
import { ObservationSchema } from './validations'
import { operationConditions } from '../db/operationConditions'
import {
  generateDocumentBodyJson,
  generateExtensionJson,
  generateIdentificationyJson,
  generateinvalidateJson,
  generateReceptorJson,
  generateSummaryJson,
} from '../../../../utility/utils'
import {
  submitMHContingencyDte,
  submitMHInvalidateDte,
  submitMHTransaction,
} from '../thunks'
import FullPageSpinner from '../../../../@core/components/spinner/Full-page-spinner'

const initialFilterProps = {
  clientCode: '',
  codeType: '',
  dteType: '',
  company: '',
  receiverEmail: '',
  receiverPhoneNumber: '',
  receiverName: '',
  startDate: moment().subtract(1, 'day'),
  endDate: null,
}

const Comments = ({
  onValidateForm,
  filteredData,
  setFilteredData,
  resetClientForm,
  resetProdForm,
}) => {
  const [filterProps, setFilterProps] = useState(initialFilterProps)
  const [areaFilterValue, setAreaFilterValue] = useState(null)
  const dispatch = useDispatch()
  //const { isPending } = useSelector((state) => state.transactions)
  const {
    bill,
    receptor,
    emisor,
    resumen,
    paymentMethods,
    terms,
    operationConditionsCatalog,
    isPending,
    relatedDoc,
  } = useSelector((state) => state.invoiceIssuance)

  const operationConditionOptions =
    Array.isArray(operationConditionsCatalog) &&
    operationConditionsCatalog.length
      ? operationConditionsCatalog
      : operationConditions
  // useForm
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialFilterProps,
      operationCondition: operationConditions[0],
      periodo: '',
      plazo: null,
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(ObservationSchema),
  })

  const operationCondition = watch('operationCondition')
  const isCredit = Number(operationCondition?.value) === 2

  const onSubmit = async (submitData, isContingency = false) => {
    const isFormValid = await resetClientForm.current.validateForm()

    if (!isFormValid) {
      onValidateForm()
      toast.error('completa el formulario de cliente')
      return
    }

    if (bill?.products?.length === 0) {
      toast.error('Debe agregar elementos a la factura')
      return
    }

    if (
      receptor?.dteType?.value === '01' &&
      bill?.total?.total > 25000 &&
      (!receptor?.receiverName ||
        !receptor?.dui ||
        !receptor?.receiverEmail)
    ) {
      onValidateForm()
      toast.error('Debe completar los campos del cliente')
      return
    }
    // En NCE/NDE los ajustes de IVA viajan por ítem y deben
    // reproducir lo que llevaba el CCFE que se ajusta. Se toma del resumen del
    // documento original cuando está disponible; si no, se cae a los totales
    // recalculados, que aplican las mismas reglas sobre el mismo cliente
    const resumenOriginal = relatedDoc?.resumenOriginal
    const noteTaxes = {
      aplicaIvaRete: resumenOriginal
        ? Number(resumenOriginal.ivaRete ?? 0) > 0
        : Number(bill.total?.retencionRenta ?? 0) > 0,
      aplicaIvaPerci: resumenOriginal
        ? Number(resumenOriginal.ivaPerci ?? 0) > 0
        : Number(bill.total?.IVAPercibido ?? 0) > 0,
    }

    const formatDocumentBody = bill.products.map((item) =>
      generateDocumentBodyJson(
        item,
        receptor.dteType?.value,
        relatedDoc,
        noteTaxes
      )
    )

    const isSubmitCredit =
      Number(submitData.operationCondition?.value) === 2
    const paymentCondition = {
      condicionOperacion: isSubmitCredit
        ? 2
        : Number(submitData.operationCondition?.value) || 1,
      periodo: isSubmitCredit ? Number(submitData.periodo) : null,
      plazo: isSubmitCredit ? submitData.plazo?.value : null,
      // Forma de pago del formulario (CAT-017). null si no se seleccionó:
      // cada tipo de DTE aplica su propio valor por defecto
      formaPago: submitData.typePayment || null,
    }

    const summaryData = generateSummaryJson(
      bill.total,
      receptor.dteType?.value,
      relatedDoc,
      paymentCondition,
      formatDocumentBody
    )
    const unifiedData = {
      // MH v2 (FSSE) pasa a usar "receptor" en lugar de "sujetoExcluido"
      receptor: generateReceptorJson(receptor),
      emisor: {
        ...emisor,
        // Campo exclusivo de FEXE. tipoItemExpor = 2 (Servicios, CAT-011).
        //
        // Con ese valor, recintoFiscal (CAT-027), tipoRegimen (CAT-033) y regimen
        // (CAT-028) van nulos, y por eso NO se envían: el backend repone las tres
        // llaves ya como null desde su plantilla base (base_json_fexe.json), que es
        // lo que exige el required del schema.
        //
        // No mandarlas explícitamente en null: processTransmitter las copia con
        // asText(), y en Jackson un null de JSON es NullNode, cuyo asText() devuelve
        // el texto "null". recintoFiscal admite 2 caracteres → rechazo del MH.
        //
        // Si algún día tipoItemExpor pasa a 1 (Bienes), las tres deben viajar con
        // valor real; ahí sí hay que incluirlas. Ver docs/GUIA-VALIDACION-DTE-v2.md §6.1
        ...(receptor.dteType?.value === '11' && {
          tipoItemExpor: 2,
        }),
      },
      cuerpoDocumento: formatDocumentBody,
      identificacion: generateIdentificationyJson(receptor.dteType?.value),
      // Las versiones nuevas de MH ya no incluyen el bloque "extension"
      ...(!['01', '03', '05', '06', '11'].includes(
        receptor.dteType?.value
      ) && {
        extension: generateExtensionJson(receptor, emisor),
      }),
      ...summaryData,
    }

    let response
    // console.log('isContingency: ', isContingency)

    // console.log('receptor:', receptor)
    console.log('unifiedData:', unifiedData)

    if (isContingency) {
      if (receptor.dteType?.value === '08') {
        const invalidBody = generateinvalidateJson(
          emisor,
          receptor,
          submitData?.conditionOperations,
          relatedDoc
        )
        response = await dispatch(submitMHContingencyDte(invalidBody))
      } else {
        response = await dispatch(submitMHContingencyDte(unifiedData))
      }
    } else if (receptor.dteType?.value === '08') {
      const invalidBody = generateinvalidateJson(
        emisor,
        receptor,
        submitData?.conditionOperations,
        relatedDoc
      )

      response = await dispatch(submitMHInvalidateDte(invalidBody))
    } else {
      response = await dispatch(submitMHTransaction(unifiedData))
    }

    if (
      response?.payload?.mmhherrors !== null ||
      response.payload?.error
    ) {
      toast.error(
        `Error al generar la transacción: ${
          response.payload?.mmhherrors?.msgDescription || ''
        } `
      )
      return
    }

    resetClientForm.current.resetForm()
    resetProdForm.current.resetProductForm()
    toast.success('Documento generado')
    setFilteredData([])
  }

  return (
    <Fragment>
      {isPending && <FullPageSpinner message="Enviando datos..." />}

      <Form
        style={{ marginBottom: '250px' }}
        onSubmit={handleSubmit((data) => onSubmit(data, false))}
      >
        <Card>
          <CardHeader className="flex-md-row flex-column align-md-items-center align-items-start border-bottom">
            <CardTitle tag="h4">Observaciones</CardTitle>
          </CardHeader>

          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col>
              <InputText
                label={'Observación'}
                errors={errors}
                options={{
                  control,
                  id: 'conditionOperations',
                  name: 'conditionOperations',
                }}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col>
              <Label className="me-1">Condición de la operación</Label>
              <Controller
                name="operationCondition"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      options={operationConditionOptions}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        if (Number(e.value) !== 2) {
                          setValue('periodo', '')
                          setValue('plazo', null)
                        }
                      }}
                    />
                    {errors.operationCondition && (
                      <FormFeedback className="d-block">
                        {errors.operationCondition.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>

            {isCredit && (
              <Col>
                <InputNumber
                  label={'Periodo'}
                  errors={errors}
                  control={control}
                  options={{
                    id: 'periodo',
                    name: 'periodo',
                    maxLength: 4,
                  }}
                />
              </Col>
            )}

            {isCredit && (
              <Col>
                <Label className="me-1">Plazo</Label>
                <Controller
                  name="plazo"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        options={Array.isArray(terms) ? terms : []}
                        value={field.value}
                        onChange={(e) => field.onChange(e)}
                      />
                      {errors.plazo && (
                        <FormFeedback className="d-block">
                          {errors.plazo.message}
                        </FormFeedback>
                      )}
                    </>
                  )}
                />
              </Col>
            )}
          </Row>

          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col>
              <Label className="me-1">Forma de pago</Label>
              <Controller
                name="typePayment"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={paymentMethods}
                      // field.value guarda solo el código (CAT-017), así que hay
                      // que resolver la opción para que el Select quede controlado
                      value={
                        (paymentMethods || []).find(
                          (method) => method.value === field.value
                        ) || null
                      }
                      onChange={(e) => {
                        field.onChange(e.value)
                      }}
                    />
                    {errors.typePayment && (
                      <FormFeedback className="d-block">
                        {errors.typePayment.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>

            <Col>
              <InputCurrency
                label={'Monto'}
                errors={errors}
                control={control}
                options={{
                  id: 'amount',
                  name: 'amount',
                  startWithCero: true,
                  maxLength: filterProps.codeType === 'dui' ? 9 : 14,
                }}
              />
            </Col>
          </Row>

          <Row
            className="mx-0"
            style={{ paddingLeft: '5%' }}
          >
            <Col className="d-flex align-items-left justify-content-sm-left my-3">
              <Button
                style={{ size: '10px', width: '200px' }}
                color="primary"
                type="submit"
              >
                Generar documento
              </Button>
            </Col>
            <Col className="d-flex align-items-left justify-content-end my-3">
              <Button
                style={{ size: '10px', width: '200px' }}
                color="danger"
                onClick={() =>
                  handleSubmit((data) => onSubmit(data, true))()
                }
              >
                Generar contingencia
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>
    </Fragment>
  )
}

export default Comments
